import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import { fmtDollar, Select, Checkbox } from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';

// ─── DEFAULT VIEWS ──────────────────────────────
const DEFAULT_VIEWS = [
  { id: "all", name: "All Deals", filters: [], system: true },
  { id: "open", name: "Open Deals", filters: [{ field: "stage_not", value: "Closed Won" }] },
  { id: "won", name: "Closed Won", filters: [{ field: "stage", value: "Closed Won" }] },
  { id: "high_value", name: "High Value ($100K+)", filters: [{ field: "val_min", value: "100000" }] },
];

function applyDealFilter(deal, f) {
  const { field, value } = f;
  if (!value) return true;
  switch (field) {
    case "stage": return (deal.stage || "").toLowerCase() === value.toLowerCase();
    case "stage_not": return (deal.stage || "").toLowerCase() !== value.toLowerCase();
    case "val_min": return (deal.val || 0) >= Number(value);
    case "val_max": return (deal.val || 0) <= Number(value);
    default: return true;
  }
}

export default function DealsPage({ onSelectContact, onSelectDeal, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const canDeleteDeals = auth.hasPermission("deals_delete");
  const { deals, contacts: CONTACTS, stages: STAGES, stageColors: STAGE_COLORS } = store;
  const [search, setSearch] = useState("");
  const [viewMode, setViewModeRaw] = useState(() => {
    try { return localStorage.getItem("cf_deals_viewMode") || "list"; } catch { return "list"; }
  });
  const setViewMode = (m) => { setViewModeRaw(m); try { localStorage.setItem("cf_deals_viewMode", m); } catch {} };
  const [sortCol, setSortCol] = useState("val");
  const [sortDir, setSortDir] = useState("desc");
  const [showAdd, setShowAdd] = useState(false);
  const [newDeal, setNewDeal] = useState({ name: "", val: "", ct: "", stage: "" });
  const [dragging, setDragging] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [stageNoteModal, setStageNoteModal] = useState(null); // { dealId, newStage }
  const [stageNoteText, setStageNoteText] = useState("");

  const handleStageDrop = async (dealId, newStage) => {
    const result = await store.updateDealStage(dealId, newStage);
    if (result === "require_note") {
      setStageNoteModal({ dealId, newStage });
      setStageNoteText("");
    }
  };

  const submitStageNote = async () => {
    if (!stageNoteModal || !stageNoteText.trim()) return;
    await store.updateDealStage(stageNoteModal.dealId, stageNoteModal.newStage, stageNoteText.trim());
    setStageNoteModal(null);
    setStageNoteText("");
  };
  const [viewKey, setViewKey] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // ── View picker ──
  const [activeViewId, setActiveViewId] = useState("all");
  const [filters, setFilters] = useState([]);
  const [showViewPicker, setShowViewPicker] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const viewPickerRef = useRef(null);

  // ── Column editor ──
  const ALL_COLUMNS = useMemo(() => [
    { id: "name", label: "Deal", required: true },
    { id: "co", label: "Company" },
    { id: "val", label: "Value", w: 120 },
    { id: "stage", label: "Stage", w: 120 },
    { id: "prob", label: "Probability", w: 90 },
    { id: "close", label: "Close Date", w: 110 },
    { id: "contact", label: "Contact", w: 130 },
  ], []);
  const DEFAULT_COLS = ["name", "co", "val", "stage", "prob", "close", "contact"];
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLS);
  const [showColEditor, setShowColEditor] = useState(false);
  const [colEditorDraft, setColEditorDraft] = useState(DEFAULT_COLS);
  const [colSearch, setColSearch] = useState("");
  const dragItem = useRef(null);
  const dragOverRef = useRef(null);

  const views = DEFAULT_VIEWS;
  const activeView = views.find(v => v.id === activeViewId) || views[0];

  useEffect(() => {
    const h = (e) => { if (viewPickerRef.current && !viewPickerRef.current.contains(e.target)) setShowViewPicker(false); };
    if (showViewPicker) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showViewPicker]);

  const switchView = useCallback((viewId) => {
    setActiveViewId(viewId);
    const view = views.find(v => v.id === viewId) || views[0];
    setFilters(view.filters ? view.filters.map(f => ({ ...f })) : []);
    setViewKey(k => k + 1);
  }, []);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const filtered = useMemo(() => {
    let result = [...deals];
    if (filters.length > 0) result = result.filter(d => filters.every(f => applyDealFilter(d, f)));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d => (d.name || "").toLowerCase().includes(q) || (d.co || "").toLowerCase().includes(q));
    }
    result.sort((a, b) => {
      const av = sortCol === "name" ? a.name : sortCol === "val" ? a.val : sortCol === "stage" ? a.stage : sortCol === "prob" ? a.prob : sortCol === "close" ? a.close : sortCol === "co" ? a.co : a.val;
      const bv = sortCol === "name" ? b.name : sortCol === "val" ? b.val : sortCol === "stage" ? b.stage : sortCol === "prob" ? b.prob : sortCol === "close" ? b.close : sortCol === "co" ? b.co : b.val;
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av || "").localeCompare(String(bv || "")) : String(bv || "").localeCompare(String(av || ""));
    });
    return result;
  }, [deals, search, sortCol, sortDir, filters]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const addDeal = () => {
    if (!newDeal.name.trim()) return;
    const ct = newDeal.ct || CONTACTS[0]?.id;
    store.createDeal({
      name: newDeal.name, val: Number(newDeal.val) || 0,
      ct: ct, co: CONTACTS.find(c => c.id === ct)?.co || "",
      stage: newDeal.stage || STAGES[0], prob: 20,
      close: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
    setNewDeal({ name: "", val: "", ct: "", stage: "" });
    setShowAdd(false);
  };

  // ── Selection ──
  const allSelected = filtered.length > 0 && filtered.every(d => selected.has(d.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(filtered.map(d => d.id))); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  const applyColumns = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem("cf_deal_columns", JSON.stringify(cols)); } catch {}
  };

  const hdrBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" };

  const confirmBulkDelete = async () => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      await store.bulkDeleteDeals([...selected]);
      setSelected(new Set());
      setShowBulkDeleteModal(false);
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* ═══ VIEW DROPDOWN — matches People page ═══ */}
      <div style={{ padding: "14px 24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div ref={viewPickerRef} style={{ position: "relative" }}>
            <button onClick={() => { setShowViewPicker(!showViewPicker); setViewSearch(""); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: showViewPicker ? "rgba(255,255,255,0.08)" : "transparent", cursor: "pointer", fontFamily: "inherit", transition: "all .15s ease" }}
              onMouseEnter={e => { if (!showViewPicker) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!showViewPicker) e.currentTarget.style.background = "transparent"; }}>
              <Icon name="briefcase" size={14} color="#FAFAFA" />
              <span style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>{activeView.name}</span>
              <span style={{ fontSize: 10, color: "#FAFAFA", opacity: 0.5, marginLeft: 2 }}>▾</span>
            </button>
            {showViewPicker && <div className="cf-dropdown-popup" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: 300, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.80)", zIndex: 99999, overflow: "hidden" }}>
              <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                <input autoFocus value={viewSearch} onChange={e => setViewSearch(e.target.value)} placeholder="Search views..." style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", padding: "8px 10px" }}>
                {views.filter(v => !viewSearch || v.name.toLowerCase().includes(viewSearch.toLowerCase())).map(v => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                    <div onClick={() => { switchView(v.id); setShowViewPicker(false); }} style={{ display: "flex", alignItems: "center", flex: 1, padding: "11px 14px", borderRadius: 8, cursor: "pointer", background: activeViewId === v.id ? "#4059AD" : "rgba(255,255,255,0.04)", color: activeViewId === v.id ? "#fff" : "#FAFAFA" }}
                      onMouseEnter={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                      onMouseLeave={e => { if (activeViewId !== v.id) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}>
                      <span style={{ fontSize: 13, fontWeight: activeViewId === v.id ? 600 : 400 }}>{v.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>}
          </div>
          <span style={{ fontSize: 12, color: "#FAFAFA", opacity: 0.5 }}>{filtered.length} deals · {fmtDollar(deals.reduce((a, d) => a + d.val, 0))}</span>
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          <button onClick={() => setViewMode("list")} style={{ padding: "7px 14px", borderRadius: "8px 0 0 8px", border: "1px solid rgba(217,219,221,0.12)", background: viewMode === "list" ? "#4059AD" : "rgba(255,255,255,0.06)", color: viewMode === "list" ? "#fff" : T.tx3, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>List</button>
          <button onClick={() => setViewMode("board")} style={{ padding: "7px 14px", borderRadius: "0 8px 8px 0", border: "1px solid rgba(217,219,221,0.12)", borderLeft: "none", background: viewMode === "board" ? "#4059AD" : "rgba(255,255,255,0.06)", color: viewMode === "board" ? "#fff" : T.tx3, fontSize: 10, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Board</button>
        </div>
      </div>

      {/* ═══ HEADER BAR — search + buttons ═══ */}
      <div style={{ display: "flex", gap: 6, padding: "10px 24px 14px", alignItems: "center", flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(0,0,0,0.15)", fontSize: 12, fontFamily: "inherit", outline: "none", color: "#FAFAFA", width: 180 }} />
        <div style={{ flex: 1 }} />
        {selected.size > 0 && canDeleteDeals && (
          <button onClick={() => setShowBulkDeleteModal(true)} style={hdrBtn}>
            Delete ({selected.size})
          </button>
        )}
        <button onClick={() => { setColEditorDraft([...visibleCols]); setColSearch(""); setShowColEditor(true); }} style={hdrBtn}>
          <Icon name="settings" size={12} color="#FAFAFA" /> Columns
        </button>
        <button onClick={() => setShowAdd(!showAdd)} style={hdrBtn}>+ Add Deal</button>
      </div>

      {/* ═══ COLUMN EDITOR MODAL ═══ */}
      {showColEditor && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowColEditor(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 700, maxWidth: "94vw", height: 520, maxHeight: "80vh", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Edit Columns</span>
              <button onClick={() => setShowColEditor(false)} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.tx3 }}>×</button>
            </div>
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              <div style={{ width: "50%", borderRight: "1px solid rgba(217,219,221,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", marginBottom: 8 }}>AVAILABLE FIELDS</div>
                  <input value={colSearch} onChange={e => setColSearch(e.target.value)} placeholder="Search fields..."
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {ALL_COLUMNS.filter(c => !c.required).filter(c => !colSearch || c.label.toLowerCase().includes(colSearch.toLowerCase())).map(col => {
                    const isActive = colEditorDraft.includes(col.id);
                    return (
                      <div key={col.id} onClick={() => { if (isActive) setColEditorDraft(p => p.filter(c => c !== col.id)); else setColEditorDraft(p => [...p, col.id]); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 2, borderRadius: 8, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 20, height: 20, borderRadius: 8, background: isActive ? "#4059AD" : "transparent", border: isActive ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {isActive && <span style={{ color: "#fff", fontSize: 11, fontWeight: 400 }}>✓</span>}
                        </div>
                        <span style={{ fontSize: 12, color: T.txt }}>{col.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ width: "50%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "12px 16px 8px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em" }}>SELECTED COLUMNS ({colEditorDraft.length})</div>
                  <div style={{ fontSize: 9, color: T.tx3, marginTop: 2 }}>Drag to reorder</div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 12px" }}>
                  {colEditorDraft.map((colId, idx) => {
                    const col = ALL_COLUMNS.find(c => c.id === colId);
                    if (!col) return null;
                    return (
                      <div key={colId} draggable
                        onDragStart={() => { dragItem.current = idx; }}
                        onDragEnter={() => { dragOverRef.current = idx; }}
                        onDragOver={e => e.preventDefault()}
                        onDragEnd={() => {
                          const from = dragItem.current, to = dragOverRef.current;
                          if (from === null || to === null || from === to) return;
                          const n = [...colEditorDraft]; const item = n.splice(from, 1)[0]; n.splice(to, 0, item);
                          setColEditorDraft(n); dragItem.current = null; dragOverRef.current = null;
                        }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 3, borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,219,221,0.08)", cursor: "grab", userSelect: "none" }}>
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", cursor: "grab", lineHeight: 1 }}>⠿</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 400, color: T.txt }}>{col.label}</span>
                        {!col.required && <button onClick={e => { e.stopPropagation(); setColEditorDraft(p => p.filter(c => c !== colId)); }}
                          style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.tx3 }}>×</button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid rgba(217,219,221,0.08)", flexShrink: 0 }}>
              <button onClick={() => setColEditorDraft([...DEFAULT_COLS])} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
              <button onClick={() => setShowColEditor(false)} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: T.txt, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => { applyColumns([...colEditorDraft]); setShowColEditor(false); setColSearch(""); }} style={{ padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 400, color: "#D9DBDD", background: T.blue, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Apply</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* ═══ ADD DEAL FORM ═══ */}
      {showAdd && (
        <div style={{ padding: "0 24px 10px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", flexShrink: 0 }}>
          <input value={newDeal.name} onChange={e => setNewDeal(p => ({ ...p, name: e.target.value }))} placeholder="Deal name *" autoFocus
            onKeyDown={e => { if (e.key === "Enter") addDeal(); }}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", width: 200, fontFamily: "inherit" }} />
          <input type="number" value={newDeal.val} onChange={e => setNewDeal(p => ({ ...p, val: e.target.value }))} placeholder="Value ($)"
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", width: 120, fontFamily: "inherit" }} />
          <Select value={newDeal.ct} onChange={e => setNewDeal(p => ({ ...p, ct: e.target.value }))} style={{ width: 180, borderRadius: 8 }}>
            <option value="">Contact...</option>
            {CONTACTS.map(c => <option key={c.id} value={c.id}>{c.fn} {c.ln}</option>)}
          </Select>
          <Select value={newDeal.stage} onChange={e => setNewDeal(p => ({ ...p, stage: e.target.value }))} style={{ width: 160, borderRadius: 8 }}>
            <option value="">Stage...</option>
            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <button onClick={addDeal} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: T.blue, color: "#D9DBDD", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", textTransform: "uppercase", letterSpacing: ".04em" }}>Save</button>
          <button onClick={() => setShowAdd(false)} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", textTransform: "uppercase", letterSpacing: ".04em" }}>Cancel</button>
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {viewMode === "list" && (<>
        <div key={"dv-" + viewKey} className="cf-page-enter" style={{ overflowX: "auto", padding: "0 24px", flex: 1 }}>
          <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid " + T.bdr, position: "sticky", top: 0, background: "#2C2E33", zIndex: 2 }}>
                <th style={{ padding: "10px 12px", width: 36 }}>
                  <Checkbox checked={allSelected} onChange={toggleAll} />
                </th>
                {visibleCols.map(colId => {
                  const col = ALL_COLUMNS.find(c => c.id === colId);
                  if (!col) return null;
                  const align = colId === "name" ? "left" : "center";
                  return (
                    <th key={col.id} onClick={() => handleSort(col.id)}
                      style={{ padding: "10px 12px", textAlign: align, fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", cursor: "pointer", width: col.w || "auto", whiteSpace: "nowrap", userSelect: "none" }}>
                      <span>{col.label}<span style={{ color: sortCol === col.id ? "#FAFAFA" : "rgba(255,255,255,0.15)", fontSize: 11, marginLeft: 4 }}>{sortIcon(col.id)}</span></span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedDeals.map(d => {
                const ct = CONTACTS.find(x => x.id === d.ct);
                const cellStyle = { padding: "8px 12px", cursor: "pointer", textAlign: "center" };
                return (
                  <tr key={d.id}
                    style={{ borderBottom: "1px solid " + T.bdr, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "8px 12px" }} onClick={e => e.stopPropagation()}>
                      <Checkbox checked={selected.has(d.id)} onChange={() => toggleOne(d.id)} />
                    </td>
                    {visibleCols.map(colId => {
                      const click = () => onSelectDeal && onSelectDeal(d.id);
                      if (colId === "name") return <td key={colId} style={{ ...cellStyle, textAlign: "left", fontSize: 12, fontWeight: 400, color: T.txt }} onClick={click}>{d.name}</td>;
                      if (colId === "co") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.tx2 }} onClick={click}>{d.co || "—"}</td>;
                      if (colId === "val") return <td key={colId} style={{ ...cellStyle, fontSize: 11, fontWeight: 400, color: "#FAFAFA" }} onClick={click}>{fmtDollar(d.val)}</td>;
                      if (colId === "stage") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.txt, textTransform: "capitalize" }} onClick={click}>{d.stage}</td>;
                      if (colId === "prob") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.txt }} onClick={click}>{d.prob}%</td>;
                      if (colId === "close") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.txt }} onClick={click}>{d.close || "—"}</td>;
                      if (colId === "contact") return <td key={colId} style={{ ...cellStyle, fontSize: 11, color: T.txt }} onClick={click}>{ct ? ct.fn + " " + ct.ln : "—"}</td>;
                      return <td key={colId} style={cellStyle} onClick={click}>—</td>;
                    })}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={visibleCols.length + 1} style={{ padding: 0 }}><div style={{ padding: 40, textAlign: "center", color: T.tx3, fontSize: 12, position: "sticky", left: 0, width: "calc(100vw - 280px)" }}>No deals found</div></td></tr>
              )}
            </tbody>
          </table>
          {/* Pagination */}
          {filtered.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexShrink: 0, borderTop: "1px solid rgba(217,219,221,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 11, color: T.tx3 }}>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {[20, 50, 100].map(size => (
                    <button key={size} onClick={() => { setPageSize(size); setCurrentPage(1); }} style={{ padding: "2px 6px", borderRadius: 4, fontSize: 10, border: "none", background: pageSize === size ? "rgba(64,89,173,0.15)" : "transparent", color: pageSize === size ? "#FAFAFA" : T.tx3, cursor: "pointer", fontFamily: "inherit" }}>{size}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === 1 ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === 1 ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => { let pn; if (totalPages <= 5) pn = i + 1; else if (currentPage <= 3) pn = i + 1; else if (currentPage >= totalPages - 2) pn = totalPages - 4 + i; else pn = currentPage - 2 + i; return (
                  <button key={pn} onClick={() => setCurrentPage(pn)} style={{ width: 28, height: 28, borderRadius: 6, fontSize: 10, border: "none", fontFamily: "inherit", background: currentPage === pn ? "rgba(64,89,173,0.15)" : "transparent", color: currentPage === pn ? "#FAFAFA" : T.tx3, cursor: "pointer" }}>{pn}</button>
                ); })}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "transparent", color: currentPage === totalPages ? "rgba(250,250,250,0.15)" : "#FAFAFA", cursor: currentPage === totalPages ? "default" : "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
              </div>
            </div>
          )}
        </div>
      </>)}

      {/* ═══ BOARD VIEW ═══ */}
      {viewMode === "board" && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          <div style={{ display: "flex", flexShrink: 0, height: 36, margin: "0 24px 8px" }}>
            {STAGES.map((stage, i) => {
              const isLast = i === STAGES.length - 1;
              const count = deals.filter(d => d.stage === stage).length;
              const total = deals.filter(d => d.stage === stage).reduce((a, d) => a + d.val, 0);
              return (
                <div key={stage} style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 200 36" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
                    <path d={i === 0 ? "M0,0 L186,0 L200,18 L186,36 L0,36 Z" : isLast ? "M0,0 L200,0 L200,36 L0,36 L14,18 Z" : "M0,0 L186,0 L200,18 L186,36 L0,36 L14,18 Z"} fill="#2C2E33" stroke="rgba(217,219,221,0.25)" strokeWidth="1" />
                  </svg>
                  <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA" }}>{stage}</span>
                    <span style={{ fontSize: 8, color: "rgba(250,250,250,0.45)" }}>{count} · {fmtDollar(total)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", flex: 1, gap: 0, overflow: isMobile ? "auto" : "hidden", margin: isMobile ? "0 8px" : "0 24px", WebkitOverflowScrolling: "touch" }}>
            {STAGES.map(stage => {
              const stageDeals = deals.filter(d => d.stage === stage);
              const isOver = dragOverStage === stage;
              return (
                <div key={stage}
                  onDragOver={e => { e.preventDefault(); setDragOverStage(stage); }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={e => { e.preventDefault(); setDragOverStage(null); const id = e.dataTransfer.getData("dealId"); if (id) handleStageDrop(id, stage); }}
                  style={{ flex: isMobile ? "0 0 200px" : 1, minWidth: isMobile ? 200 : 0, display: "flex", flexDirection: "column", borderRight: "1px solid rgba(217,219,221,0.06)", overflowY: "auto", padding: "6px 6px", background: isOver ? "rgba(64,89,173,0.06)" : "transparent", transition: "background 0.15s" }}>
                  {stageDeals.map(d => {
                    const ct = CONTACTS.find(x => x.id === d.ct);
                    return (
                      <div key={d.id} draggable
                        onDragStart={e => { e.dataTransfer.setData("dealId", d.id); setDragging(d.id); }}
                        onDragEnd={() => setDragging(null)}
                        onClick={() => onSelectDeal && onSelectDeal(d.id)}
                        style={{ padding: "10px 12px", marginBottom: 4, borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.10)", cursor: "grab", userSelect: "none", opacity: dragging === d.id ? 0.5 : 1 }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.25)"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(217,219,221,0.10)"}>
                        <div style={{ fontSize: 12, fontWeight: 400, color: T.txt, marginBottom: 2 }}>{d.name}</div>
                        {d.co && <div style={{ fontSize: 10, color: T.txt, marginBottom: 4, opacity: 0.5 }}>{d.co}</div>}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 13, fontWeight: 400, color: T.grn }}>{fmtDollar(d.val)}</span>
                          <span style={{ fontSize: 9, color: T.txt, opacity: 0.4 }}>{d.prob}%</span>
                        </div>
                        {ct && <div style={{ fontSize: 9, color: T.txt, opacity: 0.4, marginTop: 4 }}>{ct.fn} {ct.ln}</div>}
                      </div>
                    );
                  })}
                  {stageDeals.length === 0 && (
                    <div style={{ padding: "20px 10px", textAlign: "center", fontSize: 11, color: T.txt, opacity: 0.3 }}>Drop here</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stage Gate Note Modal */}
      {stageNoteModal && ReactDOM.createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}
          onClick={() => setStageNoteModal(null)}>
          <div style={{ background: "#2C2E33", borderRadius: 12, padding: 24, width: 420, maxWidth: "90vw", border: "1px solid rgba(250,250,250,0.08)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Stage Change Note Required</div>
            <div style={{ fontSize: 12, color: "#a0a3ab", marginBottom: 16 }}>
              Add a note explaining why this deal is moving to <span style={{ color: "#FFB547" }}>{stageNoteModal.newStage}</span>
            </div>
            <textarea
              value={stageNoteText}
              onChange={e => setStageNoteText(e.target.value)}
              placeholder="Why is this deal moving to this stage?"
              autoFocus
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(250,250,250,0.1)", background: "rgba(250,250,250,0.04)", color: "#FAFAFA", fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", outline: "none" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={() => setStageNoteModal(null)}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(250,250,250,0.1)", background: "transparent", color: "#a0a3ab", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={submitStageNote} disabled={!stageNoteText.trim()}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: stageNoteText.trim() ? "#4059AD" : "#535C6E", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: stageNoteText.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                Move Deal
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ BULK DELETE CONFIRMATION ═══ */}
      {showBulkDeleteModal && ReactDOM.createPortal(
        <div onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E53935", fontSize: 14, fontWeight: 500 }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 400, color: T.txt }}>Delete {selected.size} deal{selected.size === 1 ? "" : "s"}?</div>
            </div>
            <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
              This permanently removes the selected deal{selected.size === 1 ? "" : "s"} and all related deal-contact associations. Activities, emails, and calls will be kept with their deal link cleared. This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleting} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={confirmBulkDelete} disabled={bulkDeleting} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E53935", background: "#E53935", color: "#FAFAFA", fontSize: 11, cursor: bulkDeleting ? "default" : "pointer", fontFamily: "inherit", opacity: bulkDeleting ? 0.6 : 1 }}>{bulkDeleting ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
